/**
 * HR amending the namelist (D-37).
 *
 * The reason is mandatory and the record is manager-visible, which is the whole design: HR can
 * fix an obvious omission without bouncing the proposal back, but never silently — the manager
 * sees who changed what and why on the proposal itself.
 *
 * Refused if it would leave the proposal unsubmittable, so this cannot be used to empty a
 * namelist.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { ErrorNote } from '@/components/bits';
import { Button, Dialog, Field, Input, Textarea } from '@/components/ui';
import type { Proposal } from '@/lib/types';

export function AmendDialog({
  open,
  proposal,
  onClose,
}: {
  open: boolean;
  proposal: Proposal;
  onClose: () => void;
}) {
  const cache = useQueryClient();
  const [reason, setReason] = useState('');
  const [search, setSearch] = useState('');
  const [add, setAdd] = useState<number[]>([]);
  const [remove, setRemove] = useState<number[]>([]);

  const roster = useQuery({
    queryKey: ['roster', proposal.department.id],
    queryFn: () => api.employees({ department_id: proposal.department.id, limit: 500 }),
    enabled: open,
  });

  const onList = useMemo(
    () => new Set(proposal.attendees.map((attendee) => attendee.employee_id)),
    [proposal.attendees],
  );

  const candidates = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (term.length < 2) return [];
    return (roster.data?.employees ?? [])
      .filter((employee) => !onList.has(employee.id) && !add.includes(employee.id))
      .filter(
        (employee) =>
          employee.full_name.toLowerCase().includes(term) ||
          employee.employee_no.toLowerCase().includes(term),
      )
      .slice(0, 8);
  }, [roster.data, search, onList, add]);

  const byId = useMemo(
    () => new Map((roster.data?.employees ?? []).map((employee) => [employee.id, employee])),
    [roster.data],
  );

  const amend = useMutation({
    mutationFn: () =>
      api.amendProposalAttendees(proposal.id, {
        ...(add.length > 0 ? { add } : {}),
        ...(remove.length > 0 ? { remove } : {}),
        reason: reason.trim(),
        expected_version: proposal.version,
      }),
    onSuccess: async () => {
      setAdd([]);
      setRemove([]);
      setReason('');
      onClose();
      await cache.invalidateQueries({ queryKey: ['proposal', proposal.id] });
      await cache.invalidateQueries({ queryKey: ['proposal-preview', proposal.id] });
    },
  });

  const nothingToDo = add.length === 0 && remove.length === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Amend the namelist"
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            busy={amend.isPending}
            disabled={nothingToDo || reason.trim() === ''}
            onClick={() => amend.mutate()}
          >
            Apply and record
          </Button>
        </>
      }
    >
      <p className="text-muted-foreground">
        The manager sees this change and your reason on their copy of the proposal (D-37).
      </p>

      <Field label="Add someone" hint="Search this department. At least two characters.">
        {(props) => (
          <Input
            {...props}
            value={search}
            placeholder="Name or employee number"
            onChange={(event) => setSearch(event.target.value)}
          />
        )}
      </Field>

      {candidates.length > 0 ? (
        <ul className="grid gap-1">
          {candidates.map((employee) => (
            <li key={employee.id} className="flex items-center justify-between gap-2 text-sm">
              <span>
                {employee.full_name}{' '}
                <span className="text-muted-foreground text-xs">{employee.employee_no}</span>
              </span>
              <Button size="sm" onClick={() => setAdd([...add, employee.id])}>
                Add
              </Button>
            </li>
          ))}
        </ul>
      ) : null}

      {add.length > 0 ? (
        <p className="text-sm">
          <span className="text-muted-foreground">Adding:</span>{' '}
          {add.map((id) => byId.get(id)?.full_name ?? `#${id}`).join(', ')}{' '}
          <button
            type="button"
            className="text-xs underline underline-offset-4"
            onClick={() => setAdd([])}
          >
            clear
          </button>
        </p>
      ) : null}

      <fieldset className="grid gap-1.5">
        <legend className="text-sm font-medium">Remove someone</legend>
        <div className="max-h-40 overflow-y-auto">
          {proposal.attendees.map((attendee) => (
            <label key={attendee.employee_id} className="flex items-center gap-2 py-0.5 text-sm">
              <input
                type="checkbox"
                className="accent-primary size-4"
                checked={remove.includes(attendee.employee_id)}
                onChange={(event) =>
                  setRemove(
                    event.target.checked
                      ? [...remove, attendee.employee_id]
                      : remove.filter((id) => id !== attendee.employee_id),
                  )
                }
              />
              {attendee.full_name}
              <span className="text-muted-foreground text-xs">{attendee.employee_no}</span>
            </label>
          ))}
        </div>
      </fieldset>

      <Field label="Reason" required>
        {(props) => (
          <Textarea
            {...props}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Crew lead was left off the list."
          />
        )}
      </Field>

      {amend.error ? <ErrorNote error={amend.error} /> : null}
    </Dialog>
  );
}
